import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getApiKeys,
  getProfile,
  getStagingToken,
  registerStagingAccount,
  switchStagingUser,
} from 'src/clients';
import { ENV } from 'src/constants';
import { loadProductConfigs, loadProductStatus } from 'src/redux/actions';
import { setAuthDataStaging, setAuthUser } from 'src/redux/actions/authActions';
import { RootState } from 'src/redux/reducers/rootReducer';
import { storage } from 'src/utilities/Storage';
import { useDomainEnv } from './useDomainEnv';
import { useDataActivation } from './useProductActiveV2';

/**
 * A hook for handling environment switching and related functionality.
 *
 * @returns An object with functions for switching between production and staging environments.
 */
export const useSwitchEnv = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { isDomainProdEnv } = useDomainEnv();
  const navigate = useNavigate();
  const { mutate } = useDataActivation();

  /**
   * Fetches the user profile.
   *
   * @returns A promise that resolves to the user profile data.
   * @throws An error if fetching the user profile fails.
   */
  const getUserProfile = async () => {
    try {
      const res = await getProfile();
      return res.data;
    } catch (error) {
      throw new Error('Failed to get user profile.');
    }
  };

  /**
   * Fetches the staging authentication data.
   *
   * @param username - The username for which to fetch the staging data.
   * @param isFromLogin - Indicates if the request is made during login.
   * @returns A promise that resolves to the staging authentication data.
   * @throws An error if fetching the staging data fails.
   */
  const getStagingAuthData = async (
    username: string,
    isFromLogin = false,
  ): Promise<any> => {
    try {
      const isDifferentAccount = isFromLogin
        ? false
        : username !== auth.userDefault?.username;
      let tokenRes: any = null;
      if (isDifferentAccount) {
        tokenRes = await switchStagingUser(username);
      } else {
        tokenRes = await getStagingToken(username);
        if (tokenRes.data === null) {
          await registerStagingAccount();
          tokenRes = await getStagingToken(username);
        }
      }
      return tokenRes.data;
    } catch (error) {
      throw new Error('Failed to get staging auth data.');
    }
  };

  /**
   * Fetches the staging API key.
   *
   * @returns A promise that resolves to the staging API key.
   * @throws An error if fetching the staging API key fails.
   */
  const getStagingApiKey = async (): Promise<string> => {
    try {
      const res = await getApiKeys({});
      const { data } = res.data;
      return data.value;
    } catch (error) {
      throw new Error('Failed to get staging api key.');
    }
  };

  /**
   * Sets the staging data for a specific user.
   *
   * @param username - The username for which to set the staging data.
   * @param isFromLogin - Indicates if the request is made during login.
   * @throws An error if setting the staging data fails.
   */
  const setStagingData = async (username: string, isFromLogin = false) => {
    try {
      dispatch({
        type: 'SET_GENERAL_LOADING',
        payload: true,
      });

      const stagingAuthData = await getStagingAuthData(username, isFromLogin);
      storage.set(
        'session_id_prod',
        storage.get('auth')?.session_id ?? auth.session_id,
      );
      dispatch({ type: 'SET_ENV', payload: ENV.STAGING });
      dispatch({ type: 'SET_TOKEN_STAGING', payload: stagingAuthData.token });
      const prefix = 'staging';
      const stagingUsername =
        !isDomainProdEnv && username.substring(0, 7) !== prefix
          ? prefix + username
          : username;

      dispatch({
        type: 'SET_USERNAME_STAGING',
        payload: stagingUsername,
      });

      const userProfile = await getUserProfile();
      const stagingApiKey = await getStagingApiKey();

      dispatch({ type: 'SET_API_KEY_STAGING', payload: stagingApiKey });
      dispatch(setAuthDataStaging(stagingAuthData));
      dispatch(setAuthUser(userProfile));
      dispatch(loadProductStatus(true));
      dispatch(loadProductConfigs(true));
    } catch (error) {
      dispatch({ type: 'RESET_ENV' });
      throw error;
    }
  };

  /**
   * Switches to the production environment.
   *
   * @param url - The URL to navigate to after switching to production.
   */
  const switchProduction = async (url = '/') => {
    try {
      dispatch({ type: 'SET_ENV', payload: ENV.PRODUCTION });
      dispatch({ type: 'SET_USER_TO_DEFAULT' });
      dispatch({
        type: 'SET_SESSION_ID',
        payload: storage.get('session_id_prod') ?? auth.session_id,
      });

      mutate();
      dispatch(loadProductConfigs(true));
      dispatch(loadProductStatus(true));
    } finally {
      navigate(url, { replace: true });
    }
  };

  /**
   * Switches to the demo environment for a specific user.
   *
   * @param username - The username for which to switch to the demo environment.
   * @param url - The URL to navigate to after switching to the demo environment.
   * @param errorUrl - The URL to navigate to in case of an error.
   */
  const switchToDemo = async (username: string, url = '/', errorUrl = '/') => {
    try {
      if (!username) return;
      await setStagingData(username);
      navigate(url, { replace: true });
    } catch (error) {
      if (error) navigate(errorUrl);
    }
  };

  /**
   * Switches to the staging environment during login for a specific user.
   *
   * @param username - The username for which to switch to the staging environment.
   */
  const switchStagingLogin = async (username: string) => {
    if (!username) throw new Error('Username is required.');
    await setStagingData(username, true);
  };

  return {
    switchProduction,
    switchStagingLogin,
    switchToDemo,
  };
};
